import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { CommonFilter, Filter, SelectFilter } from '@vk-hr-tek/core/filter';

import { PostData, NewsData } from '@app/gen/dashboard';

import { MappedFeedPostSettings } from '../../types';

const MAX_FILTER_WIDTH: CommonFilter['width'] = 12;

const EXCLUDED_FILTER_KEY = ['type'];

@injectable()
export class FeedPostsMapper {
  constructor(private format: FormatService) {}

  processFilters(
    filters: Filter[] | null,
    type: MappedFeedPostSettings['type'],
  ): Filter[] {
    if (!filters) return [];

    const filteredFilters = filters
      .filter(({ key }) => !EXCLUDED_FILTER_KEY.includes(key))
      .map((filter) => {
        switch (filter.key) {
          case 'title_search':
            if (type === 'posts') {
              return {
                ...filter,
                fullWidth: true,
                width: MAX_FILTER_WIDTH,
                clearable: true,
              };
            } else if (type === 'news') {
              return {
                ...filter,
                clearable: true,
              };
            }
            return filter;
          default:
            return filter;
        }
      });

    return filteredFilters;
  }

  processTabOptions(filters: Filter[] | null): SelectFilter['options'] {
    if (!filters) return [];
    const typeFilter = filters.find(
      (filter) => filter.key === 'type' && filter.type === 'select',
    );
    if (!typeFilter || typeFilter.type !== 'select' || !typeFilter.options) {
      return [];
    }
    return [...typeFilter.options].reverse();
  }

  mapPost(post: PostData, pinnedPostId: string): MappedFeedPostSettings {
    return {
      id: post.id,
      type: 'posts',
      title: post.title,
      formattedDate: this.format.toDateFullMonthTime(post.created_at),
      ...(post.published_at
        ? {
            formattedPublishedAtDate: this.format.toDateFullMonthTime(
              post.published_at,
            ),
          }
        : {}),
      authorName: this.format.toShortName(
        post.author.first_name,
        post.author.middle_name,
        post.author.second_name,
      ),
      avatar:
        (post.author.second_name[0] ?? '') + (post.author.first_name[0] ?? ''),
      authorId: post.author.id,
      coverAttachmentId: post.community.avatar_attachment_id || '',
      summary: post.summary,
      isPinned: post.id === pinnedPostId,
      categoryName: post.community.title,
      communityId: post.community.id,
      communitySlug: post.community.slug,
    };
  }

  mapNews(news: NewsData, pinnedNewsId: string): MappedFeedPostSettings {
    return {
      id: news.id,
      type: 'news',
      title: news.title,
      formattedDate: this.format.toDateFullMonthTime(news.published_at),
      authorName: this.format.toShortName(
        news.author.first_name,
        news.author.middle_name,
        news.author.second_name,
      ),
      avatar: `${news.author?.second_name[0] || ''}${
        news.author?.first_name[0] || ''
      }`.toLowerCase(),
      authorId: news.author.user_id,
      categoryName: news.category?.name,
      isImportant: news.is_important,
      coverAttachmentId: news.cover_att_id,
      summary: news.announcement,
      isPinned: news.id === pinnedNewsId,
    };
  }

  processMapPostsList(
    posts: PostData[],
    pinnedPostId: string,
  ): MappedFeedPostSettings[] {
    return posts.map((post) => this.mapPost(post, pinnedPostId));
  }

  processMapNewsList(
    newsList: NewsData[],
    pinnedNewsId: string,
  ): MappedFeedPostSettings[] {
    return newsList.map((news) => this.mapNews(news, pinnedNewsId));
  }
}
